import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { toCamelCase, webStorage } from 'sidecar-utilities'
import { getExpenseTitle } from 'sidecar-member-components/utils'
import {
  Bubble,
  Button,
  ButtonContainer,
  FileDropzone,
  Icon,
  IconButton,
  Select,
  TextArea,
  TextField,
} from 'sidecar-components'

import { useMemberCommsFormik } from 'hooks/useMemberCommsFormik'
import { useMemberComms } from 'hooks/useMemberComms'
import { ImproveSearchResultsFields } from './ImproveSearchResultsFields'

import { validateFileSize } from 'utils/files'
import { hasHipaaAccess } from 'utils/hipaa'
import { GFEBubble } from 'components/inbox/GFEBubble'

export const RequestForm = ({
  ashSessionId = '',
  clearNewMessage,
  clearPopTartHistory,
  clearRequestType,
  postMessage,
  members = [],
  searchResults,
  fetchExpenseList,
  fetchMemberCommsMessages,
  newRequestId = '',
  newRequestBusy = false,
  newRequestError,
  requestType = {},
  resetExpenseSearch,
  searchResultsBusy,
  searchResultsEnd,
  selectedSpecialty = '',
  // this is passed from the app level because it needs to be in the DialogRoot
  setOpen,
  setAshSessionId,
  setSelectedSpecialty,
  setShouldAlert,
  workingExpense,
  memberOptions,
  suggestedMember,
}) => {
  // featureFlags
  const featureFlags = useFlags()
  const memberCommsImproveSearchResultsFlag =
    featureFlags['member-comms-improve-search-results']

  // working expense to be used as the suggested expense
  const workingExpenseUuid = workingExpense?.uuid

  // EXPENSE OPTIONS: State to handle setting the expenses and filtered expenses lists
  // These are used to filter the expenses based on the selected member
  // and to update the form options when the selected member changes
  // We need to set the expenses when the selected member changes
  // NOTE: This must be defined before useMemberCommsFormik hook
  const [expenses, setExpenses] = useState([])

  // History is used to route the member to the request detail page
  const navigate = useNavigate()

  const { closePoptart } = useMemberComms()

  const {
    attachmentSubtext = '',
    showAttachmentsSection = true,
    fields = [],
    messageSubtext = '',
    messageTitle = 'Message',
    requestTypeId = '',
    title = '',
    cardLimitIncrease = false,
  } = requestType

  // Reset the expense search when the component mounts
  // This actually fetches the expenses despite being called "reset" :shrug:
  useEffect(() => {
    resetExpenseSearch()
    // Clear the new message to prevent it from persisting between request
    // This also clears the error message and disabled submit btn state
    clearNewMessage()
    // fetch initial expense list
    fetchExpenseList()
  }, [])

  // Files Section
  const [files, setFiles] = useState([])
  const [fileSizeWarning, setFileSizeWarning] = useState('')

  // Suggested expense section
  const [suggestedExpense, setSuggestedExpense] = useState(null)

  // if on the expense page while, use the working expense as the suggessted expense from the searchList
  useEffect(() => {
    if (!workingExpenseUuid) return
    // check against search results, if the member changes, the search results will also change
    // so run again to get the new suggested expense

    setSuggestedExpense(
      searchResults.find(expense => expense.uuid === workingExpenseUuid),
    )
  }, [searchResults, workingExpenseUuid])

  const formik = useMemberCommsFormik({
    ashSessionId,
    suggestedMember,
    suggestedExpense,
    selectedSpecialty,
    postMessage,
    members,
    searchResults,
    expenses,
    fields,
    title,
    requestTypeId,
    featureFlags,
  })

  const {
    values,
    errors,
    dirty,
    setFieldValue,
    handleChange: handleFieldChange,
    handleSubmit,
    isValid,
  } = formik

  const {
    selectedMember,
    subject,
    selectedExpense,
    drug,
    service,
    serviceDate,
    provider,
    message,
    attachments,
  } = values

  const {
    selectedMember: selectedMemberErrors,
    subject: subjectErrors,
    selectedExpense: selectedExpenseErrors,
    drug: drugErrors,
    message: messageErrors,
    service: serviceErrors,
  } = errors

  useEffect(() => {
    if (setShouldAlert) {
      setShouldAlert(dirty)
    }
  }, [dirty])

  useEffect(() => {
    // Skip expense filtering if this request type doesn't use the expense field
    // and set the expenses to an empty array
    if (!fields.includes('expense')) {
      setExpenses([])
      return
    }

    // Filter expenses when selected member changes
    // This must come below the formik initialization
    if (selectedMember) {
      const filtered = searchResults
        .filter(
          expense =>
            hasHipaaAccess('PHI', expense.memberUuid) &&
            expense.memberUuid === selectedMember,
        )
        .map(expense => ({
          sublabel: `Expense #${expense.id}`,
          value: expense.uuid,
          label: getExpenseTitle({
            title: expense.title,
            merchantName: expense.merchantName,
            id: expense.id,
          }),
        }))

      // this allows users to deselect an expense or submit
      // a ticket without an expense
      const noExpenseOption = {
        label: 'No specific expense',
        value: '-',
      }
      // Hide the no expense option for claims submission and general claims request
      if (
        requestType.requestType !== 'Claims Submission' &&
        requestType.requestType !== 'General Claims Request'
      ) {
        filtered.push(noExpenseOption)
      }
      setExpenses(filtered)
    }
  }, [selectedMember, searchResults])

  // State to handle the busy state when for the timeout
  const [isBusy, setIsBusy] = useState(false)

  useEffect(() => {
    // Only continue if the request type isn't an empty string
    if (newRequestId) {
      // Set the busy state to true so the submit button remains disabled
      // during the timeout and after newRequestBusy is set to true
      setIsBusy(true)

      // A timeout is needed because it takes a little time the Jira comments
      // to be added to the newly created message.
      // Two comments are added, on by the BE and on by Jira automation
      setTimeout(() => {
        // Fetch/re-fetch the requests so the newly created request is include
        // This is required for the request detail page to load correctly
        fetchMemberCommsMessages()

        // Clear the request type, history, new message, specialty and busy
        // state so they do not persist to the next request
        clearPopTartHistory()
        clearRequestType()
        clearNewMessage()
        setIsBusy(false)
        setSelectedSpecialty('')
        // The deep-link params survive in the address bar, so without this a
        // second ticket from the same visit claims the same conversation.
        setAshSessionId('')

        if (cardLimitIncrease) {
          closePoptart()
        } else {
          // Original behavior: Close poptart and navigate to detail page
          // Close the poptart. If setOpen does not exist,
          // it means the form is on a page instead of in the poptart
          if (setOpen) {
            setOpen(false)
          }

          // Route the user to the new message detail page using the ID in the response.
          // The request id will always be returned if the request is successful
          navigate(`/inbox/${newRequestId}/detail`)
        }
      }, 3200)
    }
  }, [newRequestId])

  // very important effect for our API requests.
  // Most of our forms contain a field to pick a member on the account,
  // but certain forms do not require this field. We just need to attach the
  // current logged in member's id when this happens so we know who submitted the ticket.
  const memberUuid = webStorage.getItem('sub')
  useEffect(() => {
    if (!fields?.includes('member')) {
      setFieldValue('selectedMember', memberUuid)
    } else if (!formik.values.selectedMember) {
      //reset the field to default otherwise
      setFieldValue('selectedMember', '')
    }
    // had to stringify fields because if fields gets set to the default destruct array,
    // its a new array each time and will trigger the effect each render loop
  }, [memberUuid, setFieldValue, JSON.stringify(fields)])

  return (
    <div data-testid="info_requestForm" id="request_form">
      <form
        // use form tag and onSubmit formik.handleSubmit to submit form
        onSubmit={handleSubmit}
        className="max-w-6xl w-full mx-auto gap-3 block"
      >
        {fields.includes('member') && (
          <>
            <h4>Who is this for?</h4>
            <Select
              className="w-full"
              condensed={true}
              testId="info_memberDropdown"
              label="Member"
              name="selectedMember"
              options={memberOptions}
              value={
                suggestedMember?.value ? suggestedMember?.value : selectedMember
              }
              error={formik.touched.selectedMember && selectedMemberErrors}
              onBlur={formik.handleBlur}
              onChange={value => {
                setFieldValue('selectedMember', value)
                setFieldValue('selectedExpense', '')
              }}
              placeholder="Select member"
            />
          </>
        )}

        {fields.includes('expense') &&
          (suggestedExpense || expenses.length > 0) && (
            <div className="mt-5">
              <h4>Which expense is this for?</h4>
              <Select
                onBottomDetection={() => {
                  !searchResultsBusy && fetchExpenseList()
                }}
                condensed={true}
                testId="info_expenseSelect"
                id="expense_select"
                label="Expense"
                name="selectedExpense"
                options={expenses}
                value={
                  suggestedExpense?.uuid
                    ? suggestedExpense.uuid
                    : selectedExpense
                }
                error={formik.touched.selectedExpense && selectedExpenseErrors}
                onBlur={formik.handleBlur}
                onChange={value => setFieldValue('selectedExpense', value)}
                placeholder="Select expense"
              />
            </div>
          )}

        {/* Formik Text Area */}
        {fields.includes('drug') && (
          <>
            <h4>What prescriptions do you need?</h4>
            <TextField
              name="drug"
              label="Drug name, dosage and quantity"
              className="w-full mb-5"
              value={drug}
              error={formik.touched.drug && drugErrors}
              onBlur={formik.handleBlur}
              onChange={handleFieldChange}
            />
          </>
        )}

        {/* Pharmacy PLA specific fields */}
        {requestTypeId === '202' && (
          <>
            <h4>
              What {selectedSpecialty.toLowerCase()} services do you need?
            </h4>
            <p>
              Providing names or CPT codes of the services will increase the
              speed and accuracy of the prices we provide.
            </p>
            <TextField
              name="service"
              label="Services"
              className="w-full mb-5"
              value={service}
              error={formik.touched.service && serviceErrors}
              onBlur={formik.handleBlur}
              onChange={handleFieldChange}
            />
            {/* The date field in Jira is a text field, so we are using 
            a text field here as well. The member can add dates as well 
            as string such as "Next week" */}
            <TextField
              name="serviceDate"
              label="Desired date of service"
              className="w-full mb-5"
              value={serviceDate}
              onBlur={formik.handleBlur}
              onChange={handleFieldChange}
            />
            <TextField
              name="provider"
              label="Desired provider name & address"
              className="w-full mb-5"
              value={provider}
              onBlur={formik.handleBlur}
              onChange={handleFieldChange}
            />
          </>
        )}

        {/* Message and subject fields */}
        {fields.includes('message') && (
          <>
            <h4>{messageTitle}</h4>
            {fields.includes('subject') && (
              <TextField
                name="subject"
                label="Subject"
                className="w-full mb-5"
                value={subject}
                error={formik.touched.subject && subjectErrors}
                onBlur={formik.handleBlur}
                onChange={handleFieldChange}
              />
            )}
            {/* The message subtext is either an array of bullet points or a string */}
            {messageSubtext &&
              (Array.isArray(messageSubtext) && messageSubtext?.length > 0 ? (
                <ul className="sh-ul-sm">
                  {messageSubtext.map(text => (
                    <li key={`info_${toCamelCase(text)}`}>{text}</li>
                  ))}
                </ul>
              ) : (
                <p>{messageSubtext}</p>
              ))}
            <TextArea
              testId="info_messageText"
              label="Write a message"
              name="message"
              value={message}
              error={formik.touched.message && messageErrors}
              onBlur={formik.handleBlur}
              onChange={handleFieldChange}
              className="w-full"
              rows={3}
            />
          </>
        )}

        {/** Improve search results fields */}
        {memberCommsImproveSearchResultsFlag &&
          fields.includes('improve_search_results') && (
            <ImproveSearchResultsFields
              formik={formik}
              values={formik.values}
              touched={formik.touched}
              errors={formik.errors}
              handleBlur={formik.handleBlur}
              handleFieldChange={handleFieldChange}
            />
          )}

        {/* Benefit amount request type bubble with GFE help center link */}
        {requestTypeId === '284' && <GFEBubble />}

        {/* Attachments section */}
        {showAttachmentsSection && (
          <>
            <h4>Attachments</h4>

            {!!attachmentSubtext && <p>{attachmentSubtext}</p>}

            {/* Attachment list */}
            {files.length > 0 &&
              files.map((file, index) => {
                return (
                  <div
                    key={file.name}
                    className="flex gap-x-2 items-center max-w-48"
                    data-testid="info_attachment"
                  >
                    <Icon
                      name="document"
                      size="xs"
                      className="color-icon-default"
                    />
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                      {file.name}
                    </div>
                    <IconButton
                      onClick={() => {
                        setFiles(files.filter((_, i) => i !== index))
                        setFieldValue(
                          'attachments',
                          attachments.filter((_, i) => i !== index),
                        )
                      }}
                      size="sm"
                      icon="x"
                      label="Remove attachment"
                      testId="btn_removeAttachment"
                    />
                  </div>
                )
              })}

            {/* Formik Attachment Uploader */}

            <FileDropzone
              className="w-full"
              testId="info_fileDropzone"
              maxFileSize="50MB"
              error={fileSizeWarning}
              handleChange={event => {
                const { validFiles } = validateFileSize(
                  event.currentTarget.files,
                  (oversizedFile, maxFileSize) => {
                    setFileSizeWarning(
                      `File ${oversizedFile.name} is too large. Please upload files smaller than ${maxFileSize.toString}`,
                    )
                  },
                )

                if (validFiles.length === event.currentTarget.files.length) {
                  setFileSizeWarning('')
                }

                setFiles([...files, ...validFiles])
                setFieldValue('attachments', [...attachments, ...validFiles])
              }}
            />
          </>
        )}

        {!!newRequestError && (
          <Bubble
            testId="info_requestError"
            canDismiss={false}
            icon="exclamation"
            sentiment="risk"
            message={newRequestError}
          />
        )}
        <ButtonContainer>
          {/* Submit Button */}
          <Button
            testId="btn_submitMessage_memberComm"
            disabled={!isValid}
            busy={newRequestBusy || isBusy}
            icon="forward"
            type="submit"
            size="large"
            width="full"
          >
            Submit
          </Button>
        </ButtonContainer>
      </form>
    </div>
  )
}
